package com.exura.features.plandefinition.domain.model.collection

import com.exura.features.firebase.domain.model.SerializableDomainModel
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.series.BaseAnchoredSeries
import com.exura.features.plandefinition.domain.model.series.BaseSeries
import dev.gitlive.firebase.firestore.CollectionReference

/**
 * Represents a logical grouping of related series within a plan.
 * Collections help organize series that serve a common purpose.
 */
data class Collection(
    override val firebaseDocId: String? = null,
    override val firebaseDocPrefix: CollectionReference? = null,
    val name: String,
    val description: String,
    val relativeDay: Int = 0,  // Collection's relative day offset
    val series: List<BaseSeries<out BasePlanElement>> = emptyList(),
    val imageUrl: String? = null,
) : SerializableDomainModel {
    fun durationInDays(): Int {
        // For empty collection return 0
        if (series.isEmpty()) return 0

        // Find the earliest start day relative to the collection
        val minSeriesStartDay = series
            .map { it.relativeDay }
            .filterNotNull()
            .minOrNull() ?: 0

        // Find the latest end day relative to the collection
        val maxSeriesEndDay = series
            .mapNotNull { series ->
                val startDay = series.relativeDay
                val seriesDuration = series.durationInDays()

                if (startDay != null && seriesDuration != null) {
                    startDay + seriesDuration - 1
                } else null
            }.maxOrNull() ?: 0

        // Calculate collection duration based on series
        return maxSeriesEndDay - minSeriesStartDay + 1
    }

    /**
     * Returns the absolute start day of this collection in the plan
     */
    fun absoluteStartDay(): Int {
        return relativeDay + (series
            .map { it.relativeDay }
            .filterNotNull()
            .minOrNull() ?: 0)
    }

    /**
     * Returns the absolute end day of this collection in the plan
     */
    fun absoluteEndDay(): Int {
        val seriesEndDay = series
            .mapNotNull { series ->
                val startDay = series.relativeDay
                val seriesDuration = series.durationInDays()

                if (startDay != null && seriesDuration != null) {
                    startDay + seriesDuration - 1
                } else null
            }.maxOrNull() ?: 0

        return relativeDay + seriesEndDay
    }
}
